import { Switch } from '@/common/ui/switch'
import { Label } from '@/common/ui/label'
import useMutationUpdateGeneralSetting from '@/modules/client-setting/api/hooks/useMutationUpdateGeneralSetting'
import useQueryGetGeneralSetting from '@/modules/client-setting/api/hooks/useQueryGetGeneralSetting'
import { useState } from 'react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { GeneralSettingQueryKeys } from '@/modules/client-setting/keys'
import useHasPermission from '@/common/hooks/useHasPermission'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { Card, CardContent } from '@/common/ui/card'
import { AlertTriangle } from 'lucide-react'
import { Alert, AlertDescription } from '@/common/ui/alert'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'

export function UseFallbackBankToggle() {
	const queryClient = useQueryClient()
	const [isLoading, setIsLoading] = useState(false)
	const [showWarningDialog, setShowWarningDialog] = useState(false)
	const { data: generalSetting } = useQueryGetGeneralSetting()
	const { mutateAsync: updateGeneralSetting } =
		useMutationUpdateGeneralSetting()
	const hasWritePermission = useHasPermission(PermissionKeys.WEB_SETTING)

	const performUpdate = async (value: boolean) => {
		setIsLoading(true)
		try {
			await updateGeneralSetting({
				payload: {
					useFallbackBank: value,
				},
			})
			toast.success(
				value
					? 'เปิดการใช้งานธนาคารแมนนวลแล้ว (ปิดการใช้งาน ธนาคารออโต้)'
					: 'ปิดการใช้งานธนาคารแมนนวลแล้ว (เปิดการใช้งาน ธนาคารออโต้)',
			)
			queryClient.invalidateQueries({
				queryKey: [GeneralSettingQueryKeys.GetGeneralSetting],
			})
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setIsLoading(false)
		}
	}

	const handleChange = async (value: boolean) => {
		if (!hasWritePermission || !generalSetting) return

		// If turning ON, show warning dialog
		if (value) {
			setShowWarningDialog(true)
		} else {
			// If turning OFF, just update directly
			await performUpdate(value)
		}
	}

	const handleConfirmEnable = async () => {
		setShowWarningDialog(false)
		await performUpdate(true)
	}

	const handleCancelEnable = () => {
		setShowWarningDialog(false)
	}

	if (!generalSetting) return null

	return (
		<>
			<Card className="border-2">
				<CardContent className="pt-6">
					<div className="space-y-4">
						<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
							<div className="space-y-1">
								<Label
									htmlFor="useFallbackBank"
									className="text-base font-semibold"
								>
									เปิดการใช้งานธนาคารแมนนวล
								</Label>
								<p className="text-sm text-muted-foreground">
									เมื่อเปิดใช้งาน ระบบจะใช้ธนาคารแมนนวลสำหรับการฝากเงิน
									แทนการใช้ ธนาคารออโต้
								</p>
							</div>
							<Switch
								id="useFallbackBank"
								checked={generalSetting?.useFallbackBank ?? false}
								onCheckedChange={handleChange}
								disabled={isLoading || !hasWritePermission}
							/>
						</div>
						{generalSetting?.useFallbackBank && (
							<Alert>
								<AlertDescription>
									<strong className="text-red-500">หมายเหตุ:</strong>{' '}
									ขณะนี้ระบบกำลังใช้ธนาคารแมนนวล สำหรับการฝากเงิน ธนาคารออโต้
									จะไม่ถูกใช้งาน
								</AlertDescription>
							</Alert>
						)}
					</div>
				</CardContent>
			</Card>

			<AlertDialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<div className="flex items-center gap-2">
							<AlertTriangle className="h-6 w-6 text-red-500" />
							<AlertDialogTitle className="text-xl">
								⚠️ คำเตือนสำคัญ
							</AlertDialogTitle>
						</div>
						<AlertDialogDescription className="space-y-4 pt-4 text-base">
							<p className="font-semibold text-foreground">
								คุณกำลังจะเปิดใช้งานธนาคารแมนนวล (สำหรับการฝากเงินเท่านั้น)
							</p>
							<div className="rounded-lg border-2 border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/20">
								<p className="text-foreground">เมื่อเปิดใช้งานธนาคารแมนนวล:</p>
								<ul className="mt-2 list-inside list-disc space-y-2 text-foreground">
									<li>
										<strong>
											ธนาคารออโต้ (Payment Gateway)
											จะไม่ถูกใช้งานสำหรับการฝากเงิน
										</strong>
									</li>
									<li>
										<strong>
											คุณจะต้องตรวจสอบสลิปและยืนยันการฝากเงินด้วยตัวเอง
										</strong>
									</li>
									<li>การฝากเงินจะไม่เป็นอัตโนมัติ</li>
									<li>ต้องมีการจัดการแบบแมนนวลทั้งหมด</li>
								</ul>
							</div>
							<div className="rounded-lg border border-blue-300 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950/20">
								<p className="text-sm font-medium text-blue-900 dark:text-blue-300">
									📌 หมายเหตุ: การตั้งค่านี้มีผลกับการฝากเงินเท่านั้น
									การถอนเงินจะไม่ได้รับผลกระทบ
								</p>
							</div>
							<p className="text-sm text-muted-foreground">
								กรุณาตรวจสอบให้แน่ใจว่าคุณมีทีมงานพร้อมที่จะตรวจสอบและยืนยันการฝากเงินด้วยตนเอง
							</p>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={handleCancelEnable}>
							ยกเลิก
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleConfirmEnable}
							className="bg-red-500 hover:bg-red-600"
						>
							เข้าใจแล้ว เปิดใช้งานธนาคารแมนนวล
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
